<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import MessageApi from 'dashboard/api/inbox/message';
import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import TemplatesPicker from '../WhatsappTemplates/TemplatesPicker.vue';

const props = defineProps({
  conversationIds: { type: Array, required: true },
});

const { t } = useI18n();
const store = useStore();
const dialog = ref(null);
const recipients = ref([]);
const selectedIds = ref([]);
const template = ref(null);
const expiredOnly = ref(false);
const results = ref([]);
const isSending = ref(false);
const hasStarted = ref(false);
const stopRequested = ref(false);

const inboxId = computed(() => recipients.value[0]?.inbox_id);
const validInbox = computed(() => {
  const inbox = store.getters['inboxes/getInbox'](inboxId.value);
  return (
    inbox?.channel_type === 'Channel::Whatsapp' &&
    recipients.value.every(item => item.inbox_id === inboxId.value)
  );
});

const eligibleRecipients = computed(() =>
  recipients.value.filter(
    item => !expiredOnly.value || item.can_reply === false
  )
);
const targets = computed(() =>
  eligibleRecipients.value.filter(item => selectedIds.value.includes(item.id))
);
const acceptedCount = computed(
  () => results.value.filter(item => item.status === 'ACCEPTED').length
);
const statusLabels = computed(() => ({
  ACCEPTED: t('BULK_ACTION.TEMPLATES.STATUS.ACCEPTED'),
  FAILED: t('BULK_ACTION.TEMPLATES.STATUS.FAILED'),
  UNKNOWN: t('BULK_ACTION.TEMPLATES.STATUS.UNKNOWN'),
  DUPLICATE: t('BULK_ACTION.TEMPLATES.STATUS.DUPLICATE'),
  NOT_SENT: t('BULK_ACTION.TEMPLATES.STATUS.NOT_SENT'),
}));

const open = () => {
  recipients.value = props.conversationIds.map(id => {
    const conversation = store.getters.getConversationById(id);
    return {
      id,
      inbox_id: conversation?.inbox_id,
      can_reply: conversation?.can_reply,
      contactId: conversation?.meta?.sender?.id,
      name: conversation?.meta?.sender?.name || `#${id}`,
    };
  });
  selectedIds.value = recipients.value.map(item => item.id);
  expiredOnly.value = false;
  template.value = null;
  results.value = [];
  hasStarted.value = false;
  stopRequested.value = false;
  dialog.value.showModal();
};

const close = () => {
  if (!isSending.value) dialog.value.close();
};

// Never automatically retry an uncertain request: it may already have created
// a message. Accepted here means queued by ChatBravo, not delivered by WhatsApp.
const send = async payload => {
  if (isSending.value || hasStarted.value || !validInbox.value) return;
  const batch = targets.value.slice();
  if (!batch.length) return;
  const messagePayload = JSON.parse(JSON.stringify(payload));
  const contacted = new Set();
  hasStarted.value = true;
  isSending.value = true;
  try {
    // Each request must finish before the next recipient can be processed.
    // eslint-disable-next-line no-restricted-syntax
    for (const recipient of batch) {
      if (stopRequested.value) break;
      if (recipient.contactId && contacted.has(recipient.contactId)) {
        results.value.push({ ...recipient, status: 'DUPLICATE' });
      } else {
        if (recipient.contactId) contacted.add(recipient.contactId);
        try {
          // Sequential requests avoid a burst; provider quotas still apply.
          // eslint-disable-next-line no-await-in-loop
          const { data } = await MessageApi.create({
            conversationId: recipient.id,
            private: false,
            ...messagePayload,
          });
          results.value.push({
            ...recipient,
            status: data.status === 'failed' ? 'FAILED' : 'ACCEPTED',
          });
        } catch (error) {
          const status = error.response?.status;
          const rejected = status >= 400 && status < 500 && status !== 408;
          results.value.push({
            ...recipient,
            status: rejected ? 'FAILED' : 'UNKNOWN',
          });
          if (!rejected || status === 429 || status === 401) {
            stopRequested.value = true;
          }
        }
        if (!stopRequested.value && recipient !== batch[batch.length - 1]) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => {
            setTimeout(resolve, 1000);
          });
        }
      }
    }
    const completedIds = new Set(results.value.map(item => item.id));
    batch.forEach(recipient => {
      if (!completedIds.has(recipient.id)) {
        results.value.push({ ...recipient, status: 'NOT_SENT' });
      }
    });
  } finally {
    isSending.value = false;
  }
};

onBeforeRouteLeave(() => !isSending.value);
onBeforeRouteUpdate(() => !isSending.value);
onBeforeUnmount(() => {
  stopRequested.value = true;
});
useEventListener(window, 'beforeunload', event => {
  if (!isSending.value) return;
  event.preventDefault();
  event.returnValue = '';
});
</script>

<template>
  <Button
    class="w-full mt-2"
    sm
    faded
    icon="i-lucide-send"
    :label="t('BULK_ACTION.TEMPLATES.OPEN')"
    @click="open"
  />
  <TeleportWithDirection to="body">
    <dialog
      ref="dialog"
      aria-labelledby="bulk-template-title"
      class="w-full max-w-2xl max-h-[90vh] p-0 rounded-xl shadow-xl bg-n-background text-n-slate-12 backdrop:bg-n-alpha-black2"
      @cancel="isSending && $event.preventDefault()"
    >
      <div class="flex flex-col gap-4 p-5">
        <header class="flex items-center justify-between gap-3">
          <h2 id="bulk-template-title" class="text-base font-semibold">
            {{ t('BULK_ACTION.TEMPLATES.OPEN') }}
          </h2>
          <Button
            ghost
            slate
            icon="i-lucide-x"
            :aria-label="t('BULK_ACTION.TEMPLATES.CLOSE')"
            :disabled="isSending"
            @click="close"
          />
        </header>
        <p class="text-sm text-n-slate-11">
          {{ t('BULK_ACTION.TEMPLATES.DESCRIPTION') }}
        </p>
        <p v-if="!validInbox" role="alert" class="text-sm text-n-amber-11">
          {{ t('BULK_ACTION.TEMPLATES.SAME_INBOX') }}
        </p>
        <template v-else-if="!hasStarted">
          <label class="flex items-center gap-2 text-sm">
            <input v-model="expiredOnly" type="checkbox" class="m-0" />
            {{ t('BULK_ACTION.TEMPLATES.EXPIRED_ONLY') }}
          </label>
          <fieldset class="m-0 p-0 border-0">
            <legend class="mb-2 text-sm font-medium">
              {{
                t('BULK_ACTION.TEMPLATES.RECIPIENTS', { count: targets.length })
              }}
            </legend>
            <ul class="m-0 list-none max-h-40 overflow-y-auto">
              <li v-for="recipient in eligibleRecipients" :key="recipient.id">
                <label class="flex items-center gap-2 py-1 text-sm break-all">
                  <input
                    v-model="selectedIds"
                    type="checkbox"
                    class="m-0"
                    :value="recipient.id"
                  />
                  {{ t('BULK_ACTION.TEMPLATES.RECIPIENT', recipient) }}
                </label>
              </li>
            </ul>
          </fieldset>
          <p v-if="!targets.length" class="text-sm text-n-amber-11">
            {{ t('BULK_ACTION.TEMPLATES.NO_RECIPIENTS') }}
          </p>
          <TemplatesPicker
            v-if="!template"
            :inbox-id="inboxId"
            @on-select="template = $event"
          />
          <template v-else>
            <p class="text-sm text-n-amber-11">
              {{ t('BULK_ACTION.TEMPLATES.SHARED_VARIABLES') }}
            </p>
            <WhatsAppTemplateParser
              :template="template"
              @send-message="send"
              @reset-template="template = null"
            >
              <template #actions="{ sendMessage, resetTemplate, disabled }">
                <footer class="flex flex-wrap justify-end gap-2">
                  <Button
                    faded
                    slate
                    :label="t('BULK_ACTION.GO_BACK_LABEL')"
                    @click="resetTemplate"
                  />
                  <Button
                    :label="
                      t('BULK_ACTION.TEMPLATES.SEND', { count: targets.length })
                    "
                    :disabled="disabled || !targets.length"
                    @click="sendMessage"
                  />
                </footer>
              </template>
            </WhatsAppTemplateParser>
          </template>
        </template>
        <template v-else>
          <p role="status" aria-live="polite" class="text-sm font-medium">
            {{ t('BULK_ACTION.TEMPLATES.PROGRESS', { count: acceptedCount }) }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ t('BULK_ACTION.TEMPLATES.RESULT_HELP') }}
          </p>
          <ul class="m-0 list-none max-h-64 overflow-y-auto">
            <li
              v-for="result in results"
              :key="result.id"
              class="flex flex-wrap justify-between gap-2 py-2 text-sm"
            >
              <span class="break-all">{{
                t('BULK_ACTION.TEMPLATES.RECIPIENT', result)
              }}</span>
              <span>{{ statusLabels[result.status] }}</span>
            </li>
          </ul>
          <Button
            v-if="isSending"
            faded
            slate
            :disabled="stopRequested"
            :label="t('BULK_ACTION.TEMPLATES.STOP')"
            @click="stopRequested = true"
          />
          <Button
            v-else
            :label="t('BULK_ACTION.TEMPLATES.CLOSE')"
            @click="close"
          />
        </template>
      </div>
    </dialog>
  </TeleportWithDirection>
</template>
